import type { Mode } from "./plateGame.ts";

/**
 * The Plate Smash leaderboard (ADR 0060 §5-6): the top scores in this browser,
 * one board per difficulty, each with the name the player typed.
 *
 * Local to the browser and guarded like bestScore.ts - every access wrapped,
 * nothing sent anywhere. Unlike the class roster (ADR 0011), names *are*
 * written to local storage here: a player typing their own name so they can
 * see it again next time is the whole feature. The privacy policy says so.
 */

const STORAGE_KEY = "mathpages.plateLeaderboard";
const LAST_NAME_KEY = "mathpages.plateLeaderboardName";

export const BOARD_SIZE = 10;
export const NAME_MAX = 16;

export interface LeaderboardEntry {
    name: string;
    score: number;
    /** The facts played, as signs: "+ x". */
    facts: string;
    /** YYYY-MM-DD, local time. */
    date: string;
}

export type Leaderboard = Record<Mode, LeaderboardEntry[]>;

function empty(): Leaderboard {
    return { easy: [], hard: [] };
}

function cleanEntry(value: unknown): LeaderboardEntry | null {
    if (!value || typeof value !== "object") {
        return null;
    }
    const e = value as Record<string, unknown>;
    if (
        typeof e.name !== "string" ||
        typeof e.score !== "number" ||
        !Number.isFinite(e.score) ||
        typeof e.facts !== "string" ||
        typeof e.date !== "string"
    ) {
        return null;
    }
    return { name: cleanName(e.name) || "?", score: e.score, facts: e.facts, date: e.date };
}

/** Sorted best-first; an earlier entry keeps its place over a later tie. */
function ranked(entries: LeaderboardEntry[]): LeaderboardEntry[] {
    return entries
        .map((entry, i) => ({ entry, i }))
        .sort((x, y) => y.entry.score - x.entry.score || x.i - y.i)
        .map(({ entry }) => entry)
        .slice(0, BOARD_SIZE);
}

export function readLeaderboard(): Leaderboard {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return empty();
        }
        const parsed = JSON.parse(stored);
        const board = empty();
        for (const mode of ["easy", "hard"] as Mode[]) {
            if (Array.isArray(parsed?.[mode])) {
                board[mode] = ranked(
                    parsed[mode].map(cleanEntry).filter((e: LeaderboardEntry | null): e is LeaderboardEntry => e !== null)
                );
            }
        }
        return board;
    } catch {
        return empty();
    }
}

function write(board: Leaderboard): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(board));
    } catch {
        // Full, disabled, or unavailable. The round still happened.
    }
}

/** Collapses whitespace and trims to the length the name box allows. */
export function cleanName(name: string): string {
    return name.replace(/\s+/g, " ").trim().slice(0, NAME_MAX).trim();
}

/** Whether a score would get onto this board. Zero never does. */
export function qualifies(board: Leaderboard, mode: Mode, score: number): boolean {
    const entries = board[mode];
    return score > 0 && (entries.length < BOARD_SIZE || score > entries[entries.length - 1].score);
}

/**
 * Adds a score and returns the new board and the entry's rank (0-based), or -1
 * if it did not place. A tie ranks below the score already there.
 */
export function addScore(mode: Mode, entry: LeaderboardEntry): { board: Leaderboard; rank: number } {
    const board = readLeaderboard();
    const name = cleanName(entry.name);
    if (!name || !qualifies(board, mode, entry.score)) {
        return { board, rank: -1 };
    }
    const saved = { ...entry, name };
    const entries = ranked([...board[mode], saved]);
    const next = { ...board, [mode]: entries };
    write(next);
    try {
        window.localStorage.setItem(LAST_NAME_KEY, name);
    } catch {
        // Only a convenience.
    }
    return { board: next, rank: entries.indexOf(saved) };
}

/** Empties both boards and forgets the pre-filled name - every name this store holds. */
export function clearLeaderboard(): Leaderboard {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(LAST_NAME_KEY);
    } catch {
        // Nothing more to do.
    }
    return empty();
}

/** The last name saved, to pre-fill the box. */
export function readLastName(): string {
    try {
        return cleanName(window.localStorage.getItem(LAST_NAME_KEY) ?? "");
    } catch {
        return "";
    }
}

export function today(now: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
